import { Children, isValidElement, useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { InfoIcon } from '../icons'
import { placeNote, type NotePlacement } from '../../lib/notePlacement'

// An instruction kept out of the way: a small (i) that opens a note with the
// explanation and "Got it". Used where a sentence of help would otherwise sit
// on the screen for good (the list's how-to beside its count). Every note in
// the app is this one: an ink card in both themes, fixed under its (i) and
// kept 16 px inside the screen (lib/notePlacement), one InfoLine per gesture.
// A tap outside, Escape, scrolling or "Got it" puts it away.

export function InfoLine({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return <li className="ml-info__line"><span className="ml-info__icon" aria-hidden="true">{icon}</span><span>{children}</span></li>
}

export function InfoButton({ label, children }: { label: string; children: ReactNode }) {
  const [place, setPlace] = useState<NotePlacement | null>(null)
  const id = useId()
  const wrap = useRef<HTMLSpanElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const open = place !== null
  const close = () => setPlace(null)

  useEffect(() => {
    if (!open) return
    const outside = (e: PointerEvent) => { if (!wrap.current?.contains(e.target as Node)) close() }
    document.addEventListener('pointerdown', outside)
    window.addEventListener('scroll', close, { passive: true })
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('pointerdown', outside)
      window.removeEventListener('scroll', close)
      window.removeEventListener('resize', close)
    }
  }, [open])

  const toggle = () => {
    if (open || !button.current) return close()
    setPlace(placeNote(button.current.getBoundingClientRect(), window.innerWidth))
  }

  const lines = Children.toArray(children).some(c => isValidElement(c) && c.type === InfoLine)
  return (
    <span className="ml-info" ref={wrap}>
      <button ref={button} type="button" className="ml-info__btn" aria-label={label} aria-expanded={open} aria-controls={id}
        onClick={toggle}>
        <InfoIcon size={18} />
      </button>
      {place && (
        <span id={id} role="note" className="ml-info__note"
          style={{ left: `${place.left}px`, width: `${place.width}px`, top: `${place.top}px`, '--ml-info-arrow': `${place.arrow}px` } as CSSProperties}
          onKeyDown={e => { if (e.key === 'Escape') close() }}>
          {lines ? <ul className="ml-info__lines">{children}</ul> : <span className="ml-info__text">{children}</span>}
          <button type="button" className="ml-info__ok" onClick={close}>Got it</button>
        </span>
      )}
    </span>
  )
}
